import type { ReactNode } from "react";
import { DepartmentFieldAccess } from "../departmentFields";
import type { QuoteCopy } from "../i18n/QuoteCopy";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { DepartmentConfig, FormState, TimeMode } from "../types";
import { NotProvided, ReviewValue, SummaryGrid, SummaryRow, SummarySection } from "./SummarySection";

class ServicesSummaryCopy {
  constructor(private readonly copy: QuoteCopy) {}

  requested(): string {
    return this.copy.t("common.requested");
  }

  tailText({ load, unload }: FormState["tail"]): string {
    if (load && unload) return this.copy.t("services.tail.loadUnload");
    if (load) return this.copy.t("services.tail.loadOnly");
    if (unload) return this.copy.t("services.tail.unloadOnly");
    return this.requested();
  }

  timeRange(type: TimeMode, from: string, until: string, fixed: string): string | null {
    if (type === "fixed" && fixed) return this.copy.t("services.time.fixedSuffix", { time: fixed });
    if (type === "window" && (from || until)) {
      return this.copy.t("services.time.windowRange", { from: from || " ", until: until || " " });
    }
    return null;
  }

  timeText(time: FormState["time"]): string {
    const parts: string[] = [];
    const load = this.timeRange(time.loadType, time.loadFrom, time.loadUntil, time.loadFixed);
    const unload = this.timeRange(time.unloadType, time.unloadFrom, time.unloadUntil, time.unloadFixed);
    if (load) parts.push(this.copy.t("services.time.load", { value: load }));
    if (unload) parts.push(this.copy.t("services.time.unload", { value: unload }));
    if (time.deliveryDate) parts.push(this.copy.t("services.time.deadline", { value: time.deliveryDate }));
    return parts.length ? parts.join(", ") : this.requested();
  }

  adrText(adr: FormState["adr"]): string {
    if (adr.adrClass && adr.un) return this.copy.t("services.adr.classUn", { class: adr.adrClass, un: adr.un });
    if (adr.adrClass) return this.copy.t("services.adr.classOnly", { class: adr.adrClass });
    if (adr.un) return this.copy.t("services.adr.unOnly", { un: adr.un });
    return this.copy.t("services.adr.yes");
  }
}

export function ServicesSummary({ form, config }: { form: FormState; config?: DepartmentConfig }) {
  const copy = useQuoteCopy();
  const labels = new ServicesSummaryCopy(copy);
  const fields = config ? new DepartmentFieldAccess(config) : null;
  const notRequired = <NotProvided text={copy.t("common.notRequired")} />;
  const adr: ReactNode = form.adr.active ? labels.adrText(form.adr) : notRequired;
  const temp: ReactNode = form.temp.active
    ? copy.t("services.temp.range", { min: form.temp.min || " ", max: form.temp.max || " " })
    : notRequired;
  const tail: ReactNode = form.tail.active ? labels.tailText(form.tail) : notRequired;
  const ftl: ReactNode = form.ftl.active ? labels.requested() : notRequired;
  const time: ReactNode = form.time.active ? labels.timeText(form.time) : notRequired;
  const extendedRefs = fields
    ? fields.hasExtendedReferences()
    : Boolean(form.ref.loadReference || form.ref.unloadReference);
  const showTemperature = !fields || fields.showsTemperature();
  const attachments: ReactNode = form.attachments.active
    ? form.attachments.files.map((file) => file.name).filter(Boolean).join(", ") || labels.requested()
    : notRequired;

  return (
    <SummarySection title={copy.t("page.services")}>
      <SummaryGrid>
        <SummaryRow label={copy.t("services.summary.adr")}>{adr}</SummaryRow>
        {showTemperature && <SummaryRow label={copy.t("services.summary.temp")}>{temp}</SummaryRow>}
        <SummaryRow label={copy.t("services.summary.time")}>{time}</SummaryRow>
        <ReferenceSummaryRows
          form={form}
          extendedRefs={extendedRefs}
          inactive={notRequired}
          requested={labels.requested()}
        />
        <SummaryRow label={copy.t("services.summary.tail")}>{tail}</SummaryRow>
        <SummaryRow label={copy.t("services.summary.ftl")}>{ftl}</SummaryRow>
        <SummaryRow label={copy.t("services.summary.attachments")}>{attachments}</SummaryRow>
      </SummaryGrid>
    </SummarySection>
  );
}

function ReferenceSummaryRows({
  form,
  extendedRefs,
  inactive,
  requested,
}: {
  form: FormState;
  extendedRefs: boolean;
  inactive: ReactNode;
  requested: string;
}) {
  const copy = useQuoteCopy();
  if (!form.ref.active) {
    return (
      <SummaryRow
        label={extendedRefs ? copy.t("services.pill.references") : copy.t("services.summary.refNotes")}
      >
        {inactive}
      </SummaryRow>
    );
  }
  if (!extendedRefs) {
    const value = [form.ref.reference, form.ref.notes].filter(Boolean).join(" — ") || requested;
    return <SummaryRow label={copy.t("services.summary.refNotes")}>{value}</SummaryRow>;
  }
  return (
    <>
      <SummaryRow label={copy.t("services.invoiceRef")}>
        <ReviewValue value={form.ref.reference} />
      </SummaryRow>
      <SummaryRow label={copy.t("services.loadingRef")}>
        <ReviewValue value={form.ref.loadReference} />
      </SummaryRow>
      <SummaryRow label={copy.t("services.unloadingRef")}>
        <ReviewValue value={form.ref.unloadReference} />
      </SummaryRow>
      {form.ref.notes ? <SummaryRow label={copy.t("services.notes")}>{form.ref.notes}</SummaryRow> : null}
    </>
  );
}
